import { TestLocators } from 'constants/Locators';

import { MUI, ThemeMode } from 'ci-esi-web-shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CustomHeaderMenu } from 'app/components/common/CustomHeaderMenu';
import { selectIsTrialRole, selectUserCountries } from 'app/containers/PrivateRoute/selectors';
import { colors } from 'styles/theme/colors';
import { UserCountry } from 'types/UserCountry';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';

import { NON_ROUTE_HEADER_IDS } from '../utils';
import { EShapeFileType, ShapefileRegion } from './constants';
import { landingPageShapefileListSaga } from './saga';
import { selectRegion } from './selectors';
import { ShapefileModal } from './ShapefileModal';
import { actions, reducer, sliceKey } from './slice';
import { getActiveMenuItem } from '..';

export interface DownloadShapefileProps {
  anchorEl: HTMLElement | null;
  onClose(): void;
  changeActiveMenuItem: (value: string) => void;
  currentPath: string;
}

const isDisabledMenuItem = (isTrialRole: boolean, userCountries: UserCountry[], region: ShapefileRegion) => {
  if (isTrialRole) {
    return true;
  }
  switch (region) {
    case ShapefileRegion.US:
      return !userCountries.includes(UserCountry.UNITED_STATES);
    case ShapefileRegion.CANADA:
      return !userCountries.includes(UserCountry.CANADA);
  }
};

const getMenuItemLocator = (isMenuOpen: boolean, IsMenuItemDisabled: boolean) => {
  if (isMenuOpen) {
    return IsMenuItemDisabled ? TestLocators.Shapefile_Visible_Disabled : TestLocators.Shapefile_Visible_Enabled;
  }

  return IsMenuItemDisabled ? TestLocators.Shapefile_Hidden_Disabled : TestLocators.Shapefile_Hidden_Enabled;
};

export function DownloadShapefile({ anchorEl, onClose, changeActiveMenuItem, currentPath }: DownloadShapefileProps) {
  useInjectReducer({ key: sliceKey, reducer });
  useInjectSaga({ key: sliceKey, saga: landingPageShapefileListSaga });
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userCountries = useSelector(selectUserCountries);
  const isTrialRole = useSelector(selectIsTrialRole);
  const open = Boolean(anchorEl);
  const region = useSelector(selectRegion);
  const [activeTab, setActiveTab] = useState(EShapeFileType.PLAY);

  const changeActiveTabHandler = (activeTab: EShapeFileType) => setActiveTab(activeTab);

  const handleClick = useCallback(
    (region: ShapefileRegion) => {
      dispatch(actions.loadShapefileList({ region, type: activeTab }));
      setIsModalOpen(true);
      onClose();
    },
    [dispatch, onClose, activeTab],
  );

  const onCloseHandler = () => {
    changeActiveMenuItem(getActiveMenuItem(`/${currentPath}`));
    onClose();
  };

  const handleModalClose = useCallback(() => {
    changeActiveMenuItem(getActiveMenuItem(`/${currentPath}`));
    setIsModalOpen(false);
  }, [currentPath, changeActiveMenuItem]);

  useEffect(() => {
    if (region) dispatch(actions.loadShapefileList({ region, type: activeTab }));
  }, [dispatch, region, activeTab]);

  const menuItems = useMemo(
    () =>
      Object.values(ShapefileRegion).map((region: ShapefileRegion) => {
        const disabled = isDisabledMenuItem(isTrialRole, userCountries, region);

        const menuItemClickHandler = () => {
          changeActiveMenuItem(NON_ROUTE_HEADER_IDS.DOWNLOADS);
          handleClick(region);
        };

        return (
          <MUI.MenuItem
            key={region}
            disabled={disabled}
            sx={{
              minWidth: 170,
              height: 36,
            }}
            onClick={menuItemClickHandler}
          >
            <MUI.Typography
              sx={{
                fontWeight: 400,
                textTransform: 'uppercase' as const,
                color: theme.palette.text.primary,
                '&:hover': { color: isDarkMode ? colors.baseGrey10 : colors.grey90 },
              }}
              variant="body2"
              data-testid={getMenuItemLocator(open, disabled)}
            >
              {t(`DOWNLOAD_SHAPEFILE.ITEMS.${region}`)}
            </MUI.Typography>
          </MUI.MenuItem>
        );
      }),
    [isTrialRole, userCountries, theme.palette.text.primary, isDarkMode, open, t, changeActiveMenuItem, handleClick],
  );

  return (
    <>
      <CustomHeaderMenu
        id="shapefiles-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onCloseHandler}
        data-testid={TestLocators.Header_Shapefiles}
        paperTestId={open ? undefined : TestLocators.Shapefiles_Menu_Closed}
        popperPlacement="bottom-start"
      >
        {menuItems}
      </CustomHeaderMenu>
      <ShapefileModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        activeTab={activeTab}
        changeActiveTab={changeActiveTabHandler}
      />
    </>
  );
}
